# Crash reporter — the client half of CrashLog (cards 6aad7bb96e6a1e18a0903539
# / 6aad7bb96e6a1e18a090353b).
#
# WHY. Until 2026-09-18 the error boundary's screen told the parent "we are
# sending the logs to our tracking system" while trackError only did
# console.error: nothing ever left the device. A tester's iOS Safari crash
# (card 6aad780ffc5cae17204c6cd4) reached nobody. This module makes every
# crash report itself: the boundary, window.onerror and unhandled promise
# rejections all post ONE payload — message, stack, route, device, memory,
# the last console lines and whatever game context the app has set — to the
# instance's CrashLog.report function, which stores it and alerts once per
# fingerprint per hour. Fire-and-forget with `keepalive`, so a page that is
# about to die still gets it out.
#
# The sink is a dynamic Schema model: same row id on QA and production
# (published), so the key below is stable. It is anonymous-callable, capped
# and rate-limited server-side; this side throttles too so a crash loop on
# one device does not become a request loop.

import bowser from 'bowser'

CRASHLOG_KEY = 'CrashLog6aad7f73f7f82201232613e1'
TAIL_LINES = 60
TAIL_CHARS = 300
MAX_PER_MINUTE = 5
DEDUPE_MS = 10000

tail = []
context = {}
installed = false
appName = 'app'
bootAt = Date.now()
sentThisMinute = 0
minuteStamp = 0
lastMessage = null
lastMessageAt = 0

# NOISE THAT IS NOT A CRASH — card 6aad8a8e6e6a1e18a090354d.
#
# The first hour of CrashLog on production was 19 rows, and 13 of them were
# these two. Neither is something the app can act on, and both are cheap to
# produce in bulk, so between them they were spending the once-per-fingerprint
# alert budget and the 5/min client throttle that a REAL crash arriving in the
# same minute on the same device needs.
#
#   'Script error.' (x11 in nine minutes, a Chromebook and a Windows PC) is what
#   window.onerror receives for an exception inside a cross-origin script served
#   without CORS headers — a third-party tag: a pixel, a chat widget, analytics.
#   The browser withholds the message, the stack and the file on purpose, so the
#   row carries nothing but the fact that something, somewhere, went wrong in
#   code that is not ours.
#
#   'React has blocked a javascript: URL' (x2) came from the ErrorScreen's own
#   markup; the hrefs are buttons now (common.coffee), and this filter is what
#   keeps any other javascript: URL anywhere from doing it again.
#
# They are DROPPED, not silenced: suppressedCounts rides along as context on the
# next report that is real, so "this device also swallowed 11 cross-origin
# script errors" is still on the row a human reads. Counts are cumulative since
# boot — a reset would make the number mean "since the last crash", which is not
# what anybody reading a crash row wants to know.
suppressedCounts = {}

noteSuppressed = (reason) ->
  suppressedCounts[reason] = (suppressedCounts[reason] or 0) + 1
  true

# A frame naming a file we could load is what separates a real error from the
# opaque cross-origin one: the browser gives a genuine same-origin throw a stack
# with a URL in it, and gives this one nothing.
#
# OUR OWN FRAMES DO NOT COUNT — card 6aaeece32aa1cd061eee0cca. The comment here
# used to say the synthesised stack looks like "at :0:0", and the filter was
# written to that shape. No browser produces it for this path. What actually
# happens is that `new Error(String(message))` inside window.onerror captures a
# stack of the REPORTER'S frames — "at window.onerror
# (clientDist/main3.73-….js:2:223357)" — which contain '://', so hasRealFrame
# answered true and the 'Script error.' suppression never fired. Eight rows and
# an alert in one minute off a play screen (fingerprint 148c83ab, 2026-09-19),
# from the very noise card 6aad8a8e set out to remove.
#
# So: judge the frames BELOW our own. A stack whose only URL frames are the
# reporter's carries no information about where anything went wrong, which is
# what "no real frame" was always meant to mean.
REPORTER_FRAME = /\b(?:window\.onerror|crash_?reporter|noteSuppressed|suppressionReason)\b/

hasRealFrame = (stack) ->
  lines = String(stack or '').split('\n')
  for line in lines
    continue unless /:\/\//.test(line)
    continue if REPORTER_FRAME.test(line)
    return true
  false

SCRIPT_ERROR = /^(?:Error:\s*)?Script error\.?$/
REACT_JS_URL = /React has blocked a javascript: URL/

# Returns the suppression reason, or null when the report should go out.
suppressionReason = (message, error) ->
  return 'crossOriginScriptError' if SCRIPT_ERROR.test(message) and not hasRealFrame(error?.stack)
  return 'reactJavascriptUrl' if REACT_JS_URL.test(message)
  null

# THE SAME QUESTION, ASKED OF THE RAW ARGUMENTS — card 6aaeece32aa1cd061eee0cca.
#
# window.onerror is handed the browser's own account of the failure before
# anybody synthesises anything, and for an opaque cross-origin script the
# browser's account is unmistakable and identical everywhere: the message is
# exactly 'Script error.', there is NO error object (that is the whole point —
# the details are withheld), and lineno is 0. Deciding here, on those three
# facts, is a decision that cannot be confused by a stack we built ourselves.
#
# Module-private and declared beside suppressionReason on purpose: the pin
# slices this whole block out of the shipping source and drives it, so the
# predicate it asserts is the predicate window.onerror runs.
isCrossOriginScriptError = (message, source, lineno, error) ->
  return false if error?
  return false unless SCRIPT_ERROR.test(String(message ? ''))
  return false unless not lineno? or Number(lineno) is 0
  true

fmt = (args) ->
  parts = for a in args
    if a instanceof Error then (a.stack or a.message or String(a))
    else if typeof a == 'object' and a?
      try JSON.stringify(a).slice(0, TAIL_CHARS) catch e then String(a)
    else String(a)
  parts.join(' ').slice(0, TAIL_CHARS)

push = (level, args) ->
  try
    tail.push "#{new Date().toISOString().slice(11, 23)} #{level}: #{fmt(args)}"
    tail.shift() while tail.length > TAIL_LINES
  catch e
    null

wrapConsole = ->
  for level in ['log', 'info', 'warn', 'error']
    do (level) ->
      orig = console[level]
      return unless typeof orig == 'function'
      console[level] = (args...) ->
        push level, args
        orig.apply console, args

# WHO CRASHED — card 6aada89a1f4a894e18d2ce0c.
#
# Owner, 2026-09-18, after a teacher crashed on /teacher and support wanted to
# apologise: "file a ticket to add account id to the crashlog — then this would
# be easy." That row carried userId '' and authModel '' — the crash was on the
# login page, before sign-in — so there was no customer to write to. Every
# production crash was anonymous to support: a customer-facing crash could not
# be turned into a follow-up without the customer writing in first, and repeat
# crashes by the same family could not be counted as one customer.
#
# TWO SOURCES, AND THE ROW ALWAYS SAYS WHICH.
#   'session'   — somebody is signed in right now; this is who.
#   'lastKnown' — nobody is signed in, but this browser has signed in before.
#                 INFERRED, and flagged as such: it names the family that
#                 usually uses the device, which is exactly what the login-page
#                 crashes need (the teacher above, the Clever sign-in crash
#                 e60ddb4, the chunk-load 29992042 — all pre-login).
#   'none'      — a fresh private window. Nothing to say, and we say nothing.
#
# PRIVACY. Ids and the account email, and nothing else. Never a form value,
# never anything out of the console tail. CrashLog itself stays admin-only —
# anonymous may call `report` and read nothing.
#   'attempted' — nobody is signed in and this browser has never signed in,
#                 but the URL that opened this page NAMED somebody. A stale QR
#                 sheet is /app?access_token=<unusable>&uid=<login id>: the
#                 token cannot authenticate, so there is no session and never
#                 was one on this device — and yet we know exactly whose link
#                 it is. Card 6aaf07728b7b7297d73a152b. Weaker than lastKnown
#                 and flagged as its own source for that reason: lastKnown is
#                 a sign-in this browser really completed, while this is only
#                 what a link claimed, on a credential the client REJECTED.
LAST_KNOWN_KEY = 'elfeetLastKnownIdentity'

# IN MEMORY, FOR THIS PAGE ONLY — never localStorage. `attempted` is not a
# fact about the device, it is a fact about the URL that opened this one page.
# Persisting it would make the next crash in the same browser claim a family
# that only ever arrived here on a bad link, which is the inference
# `identitySource` exists to prevent.
attempted = null

# Called from the boot path when it installs a credential the URL carried, so
# a crash that happens before anything authenticates still says whose link it
# was. Best-effort, never throws, and it does NOT displace a real session:
# identityBlock ranks it last.
export noteAttemptedIdentity = (identity) ->
  try
    return false unless identity? and (identity.loginId or identity.studentId)
    keep = {}
    keep[k] = String(identity[k]) for k in ['loginId', 'studentId', 'authModel'] when identity[k]?
    attempted = keep
    true
  catch e
    false

# What the boot path recorded, if anything. Exported so the payload can fall
# back to the token's model for `authModel` on a row with no session.
export getAttemptedIdentity = -> attempted

# The form every shell actually has: the URL's own query params. All three
# boot paths (/app, /teacher, /org) install a URL-borne credential the same
# way, so the rule for reading one lives HERE rather than three times over —
# including what the token's prefix means, which is auth framing and not
# something a route file should be deciding for itself.
export noteAttemptedIdentityFromUrlParams = (params) ->
  try
    p = params or {}
    return false unless p.access_token? and (p.uid? or p.studentId?)
    # "<Model>Auth_<token>" is the framing the server's parseAuthHeader
    # requires, so the model is READ, not guessed. A link minted before the v3
    # cutover carries no prefix and contributes nothing — which is correct:
    # those are precisely the tokens that cannot authenticate.
    tokenModel = (String(p.access_token).match(/^(.+?)Auth_/) or [])[1]
    # A studentId that swallowed the fragment is not an id (card
    # 6aa6dbae617b8dc32cc6b4c0's shape); every shell already guards on it.
    studentId = p.studentId if p.studentId? and !/#/.test(p.studentId)
    noteAttemptedIdentity {loginId: p.uid, studentId: studentId, authModel: tokenModel}
  catch e
    false

# Every read and write of storage is wrapped: private windows, blocked site
# data and embedded WebViews all throw on access, and a crash reporter that
# throws while reporting a crash is the worst possible failure.
readLastKnown = ->
  try
    raw = window.localStorage?.getItem(LAST_KNOWN_KEY)
    return null unless raw
    parsed = JSON.parse raw
    if parsed? and typeof parsed == 'object' then parsed else null
  catch e
    null

# Called from loopback_api on every successful sign-in.
export rememberIdentity = (identity) ->
  try
    return false unless identity? and (identity.loginId or identity.accountId or identity.email)
    keep = {}
    keep[k] = identity[k] for k in ['loginId', 'accountId', 'email', 'role'] when identity[k]?
    keep.at = new Date().toISOString()
    window.localStorage?.setItem LAST_KNOWN_KEY, JSON.stringify(keep)
    true
  catch e
    false

# Called from loopback_api on an EXPLICIT logout only — not on a token expiry.
# A session that timed out is not the family leaving the device, and forgetting
# them there would make the next login-page crash anonymous again, which is the
# case this whole card exists for.
export forgetIdentity = ->
  try
    # The attempt goes with it. Somebody who signs out in this tab has told us
    # who they are not; a link they arrived on earlier must not outlive that.
    attempted = null
    window.localStorage?.removeItem LAST_KNOWN_KEY
    true
  catch e
    false

# parent / teacher / org / admin / student. `appName` is the shell we are in,
# which is the only thing that distinguishes an org user from a teacher when
# the account type does not.
roleOf = (app, account) ->
  try
    return 'student' if app?.loopbackAPI?.isStudentSession?()
    switch account?.type
      when 'teacher' then 'teacher'
      when 'organization' then 'org'
      else
        switch appName
          when 'teacher' then 'teacher'
          when 'org' then 'org'
          when 'admin' then 'admin'
          else 'parent'
  catch e
    undefined

# Best-effort, every field optional, nothing here may throw.
identityBlock = ->
  try
    app = window.app
    login = app?.login
    account = app?.account ? login?.account
    if login? or account?
      student = try app?.getActiveStudent?() catch e then null
      out = {identitySource: 'session'}
      out.loginId = String(login.id) if login?.id?
      out.accountId = String(account.id) if account?.id?
      # `name` is the account's email on this model (Account.json labels it
      # Email); login.email is the person's.
      out.email = String(login?.email ? account?.name) if (login?.email ? account?.name)?
      out.role = roleOf(app, account)
      out.studentId = String(student.id) if student?.id?
      out.organizationId = String(account.organizationId) if account?.organizationId?
      out.schoolId = String(account.schoolId) if account?.schoolId?
      out.districtId = String(account.districtId) if account?.districtId?
      out.cleverId = String(account?.cleverId ? login?.cleverId) if (account?.cleverId ? login?.cleverId)?
      return out
    last = readLastKnown()
    if last?
      out = {identitySource: 'lastKnown'}
      out[k] = String(last[k]) for k in ['loginId', 'accountId', 'email', 'role'] when last[k]?
      out.lastSeenAt = last.at if last.at?
      return out
    # LAST, and only when the other two have nothing. A device that has really
    # signed in is named by what it did, never by what a link claimed.
    if attempted?
      out = {identitySource: 'attempted'}
      out[k] = attempted[k] for k in ['loginId', 'studentId'] when attempted[k]?
      return out
    {identitySource: 'none'}
  catch e
    {identitySource: 'none'}

export getIdentity = -> identityBlock()

export setContext = (patch) ->
  context = Object.assign {}, context, (patch or {})
  context

export getContext = -> context

# What the reporter has dropped as noise since boot, by reason. Exported so a
# support session can ask the page directly rather than inferring it from the
# absence of rows.
export getSuppressedCounts = -> Object.assign {}, suppressedCounts

platform = ->
  try
    r = bowser.getParser(window.navigator.userAgent).parse().parsedResult
    {os: r.os?.name, osVersion: r.os?.version, browser: r.browser?.name, browserVersion: r.browser?.version, device: r.platform?.type}
  catch e
    {}

memory = ->
  m = {}
  try
    if performance?.memory?
      m.usedJSHeapMB = Math.round(performance.memory.usedJSHeapSize / 1048576)
      m.totalJSHeapMB = Math.round(performance.memory.totalJSHeapSize / 1048576)
    m.deviceMemoryGB = navigator.deviceMemory if navigator.deviceMemory?
  catch e
    null
  m

route = ->
  try
    (window.location.pathname + window.location.hash).replace(/\?.*$/, '').slice(0, 500)
  catch e
    ''

version = ->
  window.config?.appVersion or window.config?.clientVersion or window.config?.version or ''

throttled = (message) ->
  now = Date.now()
  minute = Math.floor(now / 60000)
  if minute != minuteStamp
    minuteStamp = minute
    sentThisMinute = 0
  return true if sentThisMinute >= MAX_PER_MINUTE
  return true if message == lastMessage and (now - lastMessageAt) < DEDUPE_MS
  sentThisMinute += 1
  lastMessage = message
  lastMessageAt = now
  false

endpoint = ->
  base = (window.config?.basePath or '').replace(/\/$/, '')
  "#{base}/api/#{CRASHLOG_KEY}/report"

# Build and send one report. Resolves true when the server accepted it,
# false otherwise. Never throws — a reporter that throws inside an error
# handler is how error handlers become crash loops.
export report = (kind, error, extra = {}) ->
  try
    message = if error?.message? then String(error.message) else String(error ? 'unknown error')
    message = "#{error.name}: #{message}" if error?.name and message.indexOf(error.name) != 0
    # Dropped BEFORE the throttle, so noise never costs a real crash its slot.
    if (reason = suppressionReason(message, error))?
      noteSuppressed reason
      return Promise.resolve(false)
    return Promise.resolve(false) if throttled(message)
    app = window.app
    payload =
      at: new Date().toISOString()
      kind: kind
      message: message.slice(0, 2000)
      stack: String(error?.stack or '').slice(0, 8000)
      componentStack: String(extra.componentStack or '').slice(0, 4000)
      route: route()
      app: appName
      version: version()
      userAgent: navigator.userAgent
      platform: platform()
      viewport: "#{window.innerWidth}x#{window.innerHeight}@#{window.devicePixelRatio or 1}"
      memory: memory()
      online: navigator.onLine != false
      consoleTail: tail.slice()
      context: Object.assign {
        minutesSinceBoot: Math.round((Date.now() - bootAt) / 6000) / 10
        studentId: (try app?.getActiveStudent?()?.id catch e then undefined)
        isApp: (try app?.isApp?() catch e then undefined)
        suppressed: (if Object.keys(suppressedCounts).length then Object.assign({}, suppressedCounts) else undefined)
      }, context, (extra.context or {})
      # With no session, the model the URL's token NAMED is the only thing that
      # says what kind of principal the link was for — a parent's Login or a
      # student's Student. It is the token's own prefix, not a guess.
      authModel: (if app?.login? then 'Login' else (attempted?.authModel or ''))
      userId: String(app?.login?.id or '')
      # Who this was — card 6aada89a1f4a894e18d2ce0c. Always sent; always says
      # which of the three sources it came from.
      identity: identityBlock()
    body = JSON.stringify {payload}
    # THE SERVER'S ANSWER IS IN THE BODY, NOT THE STATUS — card
    # 6aadb69ed60dadb9b2befafa.
    #
    # `CrashLog.report` has a global flood guard: past 120 reports a minute it
    # stores NOTHING and returns `{ok: false, dropped: 'rate'}` — with HTTP
    # 200, because it is a well-formed answer to a well-understood request, not
    # a server error. Keying on `res.ok` (the HTTP flag) read that as success.
    #
    # What made it matter is what the flag drives: error_boundary sets
    # `reported` from this promise and renders "This error has been reported to
    # our team." So in exactly the situation the guard exists for — a device
    # crash-looping, the reports being dropped — every one of those families
    # was told their crash was on its way to us, and none of them was. They
    # stop worrying and we have nothing. A crash loop is when we most need the
    # family to write in.
    #
    # So: the report is REPORTED when the server says it stored it. A 2xx whose
    # body does not say `ok: true` is not an acceptance. Unparseable body, a
    # 5xx, a network failure — all false, and the screen has honest copy for
    # it ("We could not report this error automatically.").
    fetch(endpoint(), {method: 'POST', headers: {'Content-Type': 'application/json'}, body: body, keepalive: true, credentials: 'omit'})
      .then (res) ->
        return false unless res.ok
        res.json().then((parsed) -> parsed?.ok == true).catch(-> false)
      .catch(-> false)
  catch e
    Promise.resolve(false)

# Install once per page: console ring buffer, window.onerror (chained, never
# replaced — remote logging chains the same way) and unhandledrejection.
export install = (name = 'app') ->
  appName = name
  return if installed
  installed = true
  wrapConsole()
  prior = window.onerror
  window.onerror = (message, source, lineno, colno, error) ->
    try
      # Decided from the RAW arguments, BEFORE an Error is synthesised — the
      # synthesised stack is ours and says nothing about the failure (card
      # 6aaeece32aa1cd061eee0cca). Still counted, so the suppression rides
      # along as context on the next report that is real.
      if isCrossOriginScriptError message, source, lineno, error
        noteSuppressed 'crossOriginScriptError'
      else
        err = error or new Error(String(message))
        err.stack ?= "#{message}\n    at #{source}:#{lineno}:#{colno}"
        report 'onerror', err
    catch e
      null
    prior?.apply this, arguments
  window.addEventListener 'unhandledrejection', (ev) ->
    try
      reason = ev?.reason
      err = if reason instanceof Error then reason else new Error(String(reason?.message or reason or 'unhandled rejection'))
      report 'unhandledrejection', err
    catch e
      null
  push 'info', ["crash reporter installed (#{name})"]
  true
